import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  Home, LayoutDashboard, Users, Receipt, TrendingUp, FileBarChart2,
  Megaphone, Settings as SettingsIcon, LogOut, Moon, HandHeart, Building2,
  Menu, X, PiggyBank, Ban,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Sheet, SheetContent } from "@/components/ui/sheet";

const commercialLinks = [
  { to: "", icon: LayoutDashboard, label: "لوحة التحكم", end: true, tid: "nav-commercial-dashboard" },
  { to: "campaigns", icon: Megaphone, label: "الحملات", tid: "nav-commercial-campaigns" },
  { to: "beneficiaries", icon: Users, label: "المستفيدون", tid: "nav-commercial-beneficiaries" },
  { to: "expenses", icon: Receipt, label: "المصروفات", tid: "nav-commercial-expenses" },
  { to: "revenues", icon: PiggyBank, label: "إضافة إيراد", tid: "nav-commercial-revenues" },
  { to: "income", icon: TrendingUp, label: "قائمة الدخل", tid: "nav-commercial-income" },
];

const charityLinks = [
  { to: "", icon: LayoutDashboard, label: "لوحة التحكم", end: true, tid: "nav-charity-dashboard" },
  { to: "campaigns", icon: Megaphone, label: "الحملات", tid: "nav-charity-campaigns" },
  { to: "beneficiaries", icon: HandHeart, label: "المستفيدون", tid: "nav-charity-beneficiaries" },
  { to: "reports", icon: FileBarChart2, label: "التقارير", tid: "nav-charity-reports" },
];

function SidebarContent({ portal, settings, user, links, onNavigate, onClose }) {
  const isCommercial = portal === "commercial";
  const PortalIcon = isCommercial ? Building2 : HandHeart;
  const portalTitle = isCommercial ? "البوابة التجارية" : "البوابة الخيرية";
  const accent = isCommercial ? "text-amber-400" : "text-amber-500";

  const go = (path) => { onNavigate(path); onClose?.(); };

  return (
    <div className="h-full bg-emerald-950 text-white flex flex-col">
      <div className="p-5 border-b border-white/10 flex items-center justify-between gap-2">
        <button
          onClick={() => go("/")}
          className="flex items-center gap-3 text-right group flex-1 min-w-0"
          data-testid="sidebar-back-home"
        >
          {settings.logo_base64 ? (
            <img src={settings.logo_base64} alt="" className="h-11 w-11 rounded-xl object-cover flex-shrink-0" />
          ) : (
            <div className="h-11 w-11 rounded-xl bg-amber-500 flex items-center justify-center flex-shrink-0">
              <Moon className="h-6 w-6 text-emerald-950" />
            </div>
          )}
          <div className="flex-1 min-w-0 overflow-hidden">
            <div
              className="font-heading font-bold text-[13px] leading-tight group-hover:text-amber-300 transition-colors break-words"
              style={{
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 2,
                overflow: 'hidden',
              }}
              title={settings.association_name}
            >
              {settings.association_name}
            </div>
            <div className="text-[10px] text-emerald-200/60 mt-0.5">الرئيسية</div>
          </div>
        </button>
        {onClose && (
          <button onClick={onClose} className="xl:hidden text-white/70 hover:text-white flex-shrink-0" data-testid="sidebar-close">
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      <div className="p-4">
        <div className="flex items-center gap-2 px-3 py-3 rounded-lg bg-white/5">
          <PortalIcon className={`h-5 w-5 ${accent}`} />
          <span className="font-heading font-bold">{portalTitle}</span>
        </div>
      </div>

      <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
        {links.map((l) => (
          <NavLink
            key={l.to}
            to={l.to}
            end={l.end}
            onClick={onClose}
            data-testid={l.tid}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                isActive
                  ? "bg-amber-500 text-emerald-950 font-bold shadow-lg shadow-amber-500/20"
                  : "text-emerald-100 hover:bg-white/10"
              }`
            }
          >
            <l.icon className="h-5 w-5" />
            <span>{l.label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="p-3 border-t border-white/10 space-y-1">
        <button
          onClick={() => go("/")}
          className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-emerald-100 hover:bg-white/10 transition-all"
        >
          <Home className="h-5 w-5" />
          <span>البوابة الرئيسية</span>
        </button>
        {user?.role === "admin" && (
          <button
            onClick={() => go("/settings")}
            data-testid="sidebar-settings-btn"
            className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-emerald-100 hover:bg-white/10 transition-all"
          >
            <SettingsIcon className="h-5 w-5" />
            <span>الإعدادات</span>
          </button>
        )}
        {user?.role === "admin" && (
          <button
            onClick={() => go("/blacklist")}
            data-testid="sidebar-blacklist-btn"
            className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-emerald-100 hover:bg-white/10 transition-all"
          >
            <Ban className="h-5 w-5" />
            <span>القائمة السوداء</span>
          </button>
        )}
        <button
          onClick={() => { localStorage.removeItem("umrah_token"); window.location.href = "/login"; }}
          data-testid="sidebar-logout-btn"
          className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-red-300 hover:bg-red-500/10 transition-all"
        >
          <LogOut className="h-5 w-5" />
          <span>تسجيل الخروج</span>
        </button>
        <div className="text-xs text-emerald-200/50 px-4 pt-3">
          {user?.name} · {user?.role === "admin" ? "مدير" : "موظف"}
        </div>
      </div>
    </div>
  );
}

export default function Layout({ portal }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState({ association_name: "جمعية العمرة" });
  const [mobileOpen, setMobileOpen] = useState(false);
  const isCommercial = portal === "commercial";
  const links = isCommercial ? commercialLinks : charityLinks;
  const portalTitle = isCommercial ? "البوابة التجارية" : "البوابة الخيرية";

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#FDFBF7] xl:flex" dir="rtl">
      {/* Desktop Sidebar */}
      <aside className="no-print hidden xl:flex w-72 sticky top-0 h-screen flex-shrink-0">
        <SidebarContent portal={portal} settings={settings} user={user} links={links} onNavigate={navigate} />
      </aside>

      {/* Mobile Top Bar */}
      <header className="no-print xl:hidden sticky top-0 z-40 bg-emerald-950 text-white flex items-center justify-between px-4 py-3 shadow-lg">
        <button
          onClick={() => setMobileOpen(true)}
          className="p-2 -mr-2 rounded-lg hover:bg-white/10"
          data-testid="mobile-menu-open"
          aria-label="فتح القائمة"
        >
          <Menu className="h-6 w-6" />
        </button>
        <div className="flex items-center gap-2 min-w-0 overflow-hidden">
          <div className="font-heading font-bold text-sm truncate">{portalTitle}</div>
          {settings.logo_base64 ? (
            <img src={settings.logo_base64} alt="" className="h-8 w-8 rounded-lg object-cover flex-shrink-0" />
          ) : (
            <div className="h-8 w-8 rounded-lg bg-amber-500 flex items-center justify-center flex-shrink-0">
              <Moon className="h-4 w-4 text-emerald-950" />
            </div>
          )}
        </div>
      </header>

      {/* Mobile Sheet */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="right" className="p-0 w-[88vw] max-w-[320px] bg-emerald-950 border-0" dir="rtl">
          <SidebarContent
            portal={portal} settings={settings} user={user} links={links}
            onNavigate={navigate} onClose={() => setMobileOpen(false)}
          />
        </SheetContent>
      </Sheet>

      {/* Content */}
      <main className="flex-1 min-w-0">
        <Outlet />
      </main>
    </div>
  );
}
